<script setup lang="ts">
import Spinner from '@/components/displays/Spinner.vue'
import { Dialog, DialogScrollContent, DialogTitle } from '@/components/shad/ui/dialog'
import { useMitreAttackStore } from '@/stores/mitre.store'
import { storeToRefs } from 'pinia'

import { computed, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// The layout mounts this modal on every page; loading the markdown stack
// eagerly would put md-editor, mermaid, katex, and prettier in the entry chunk.
const MarkdownDisplay = defineAsyncComponent(
  () => import('@/components/displays/MarkdownDisplay.vue'),
)

const mitreStore = useMitreAttackStore()
const { contents } = storeToRefs(mitreStore)

const route = useRoute()
const router = useRouter()

const modalTechniqueId = computed({
  get: () => route.query.modalTechniqueId,
  set: (val) =>
    router.replace({
      query: { ...route.query, modalTechniqueId: val || undefined },
    }),
})

// MODAL / POPOVER DIALOG - GLOBAL
const modalTechnique = computed(() => {
  let match = undefined
  if (modalTechniqueId.value && mitreStore.fetched) {
    for (const matrixDomain of contents.value) {
      match = matrixDomain.objects?.find(
        (obj) =>
          obj.type === 'attack-pattern' &&
          obj.mitre_id === modalTechniqueId.value,
      )
      if (match) {
        break
      }
    }
  }
  return match
})
const showModal = computed(
  () => !!(modalTechniqueId.value && modalTechnique.value),
)

// @ts-ignore
const hideModal = () => modalTechniqueId.value = undefined
</script>

<template>
  <Dialog
    modal
    :open="showModal"
    maximizable
    :header="
      modalTechnique?.name
        ? `MITRE Technique: ${modalTechnique.name} (${modalTechnique.mitre_id})`
        : 'MITRE Technique Detail'
    "
    @update:open="  hideModal"
    @update:visible="modalTechniqueId = $event || undefined"
  >
    <DialogScrollContent class="max-w-[800px] mt-3 pb-6">
      <DialogTitle>
        <div v-if="modalTechnique?.name">
          MITRE Technique: {{ modalTechnique.name }}
          <span class="text-subdued">{{ modalTechnique.mitre_id }}</span>
        </div>
      </DialogTitle>
      <Spinner v-if="mitreStore.loading" />
      <div v-else-if="modalTechnique">
        <div v-if="modalTechnique.x_mitre_deprecated"
          class="bg-warn text-black font-extrabold text-sm"
        >
          Deprecated
        </div>
        <div
          v-if="modalTechnique.revoked"
          class="bg-warn text-black font-extrabold text-xs rounded-md px-1.5 py-1">
          Revoked
        </div>
        <div v-if="modalTechnique.x_mitre_platforms?.length">
          <p class="my-2 font-extrabold">Platforms:</p>
          <div class="flex gap-2">
            <div
              v-for="platform in modalTechnique.x_mitre_platforms.sort()"
              :key="platform"
              class="bg-primary text-black font-extrabold text-xs rounded-md px-1.5 py-1"
            >
              {{ platform }}
            </div>
          </div>
        </div>
        <hr class="my-3" />
        <div v-if="modalTechnique.description">
          <p class="my-2 font-extrabold">Description</p>
          <MarkdownDisplay
            :id="`modal-${modalTechnique.id}`"
            :value="modalTechnique.description"
          />
        </div>
        <div v-if="modalTechnique.x_mitre_detection">
          <hr class="my-3" />
          <p class="my-2 font-extrabold">Detection</p>
          <MarkdownDisplay
            :id="`modal-${modalTechnique.id}`"
            :value="modalTechnique.x_mitre_detection"
          />
        </div>
        <div v-if="modalTechnique.external_references?.length">
          <hr class="my-3" />
          <p class="my-2 font-extrabold">References</p>
          <ul class="list-disc">
            <li
              v-for="ref in modalTechnique.external_references"
              :key="ref.url"
              class="m-1 ml-6"
            >
              <a
                :href="ref.url"
                target="_blank"
                class="!text-base !text-link-color"
              >
                {{
                  ref.source_name.replace(
                    'mitre-attack',
                    'MITRE ATT&CK definition',
                  )
                }}
              </a>
              <p v-if="ref.description" class="!text-sm text-subdued">
                {{ ref.description }}
              </p>
            </li>
          </ul>
        </div>
      </div>
      <p v-else>
        <i>No MITRE modalTechnique found with id </i>
        <b>{{ modalTechniqueId }}</b
        >.
      </p>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped></style>
